import { useCallback, useEffect, useMemo, useState } from "react";

import { ColumnDefinition, EmployeeData, TableFilter } from "../EmployeeDataDrawer";

interface UseEmployeeTableStateProps {
  localPersonnelData: EmployeeData[];
  columns: ColumnDefinition[];
}

interface UseEmployeeTableStateReturn {
  // Pagination
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  rowsPerPage: number;

  // Sorting
  sortedData: EmployeeData[];

  // Filtering
  filters: TableFilter[];
  filteredData: EmployeeData[];
  paginatedData: EmployeeData[];
  uniqueValuesByColumn: Record<string, string[]>;
  filterMenuAnchorEl: HTMLElement | null;
  filterMenuColumnKey: string | null;
  filterMenuValue: string;
  setFilterMenuValue: React.Dispatch<React.SetStateAction<string>>;

  // Selection
  selectedKeys: Set<string>;
  visibleKeys: string[];
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;

  // Pagination info
  totalRecords: number;
  pageStart: number;
  pageEnd: number;

  // Actions
  addFilter: (columnKey: string, value: string) => void;
  removeFilter: (id: string) => void;
  toggleFilterValue: (columnKey: string, value: string) => void;
  clearFilters: () => void;
  clearColumnFilters: (columnKey: string) => void;
  openColumnFilterMenu: (columnKey: string, anchorEl: HTMLElement) => void;
  closeColumnFilterMenu: () => void;
  handleApplyColumnFilter: () => void;
  handleSelectColumnValue: (value: string) => void;
  handleClearColumnFilters: () => void;
  toggleRowSelection: (rowKey: string, isSelected: boolean) => void;
  toggleSelectAllVisible: (checked: boolean) => void;
  resetSelection: () => void;
}

export const useEmployeeTableState = ({
  localPersonnelData,
  columns,
}: UseEmployeeTableStateProps): UseEmployeeTableStateReturn => {
  // Pagination state
  const [page, setPage] = useState(0);
  const rowsPerPage = 100;

  // Filter state
  const [filters, setFilters] = useState<TableFilter[]>([]);
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [filterMenuColumnKey, setFilterMenuColumnKey] = useState<string | null>(
    null,
  );
  const [filterMenuValue, setFilterMenuValue] = useState<string>("");

  // Selection state
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set<string>());

  // Sorted data (by key)
  const sortedData = useMemo(() => {
    return [...localPersonnelData].sort((a, b) => {
      const keyA = Number(a.key) || 0;
      const keyB = Number(b.key) || 0;


return keyA - keyB;
    });
  }, [localPersonnelData]);

  // Unique values by column for filter dropdown
  const uniqueValuesByColumn = useMemo(() => {
    const result: Record<string, string[]> = {};

    columns.forEach((column) => {
      const seen = new Set<string>();

      sortedData.forEach((row) => {
        const value = row[column.key as keyof EmployeeData];

        if (value !== undefined && value !== null) {
          const strValue = String(value).trim();

          if (strValue) {
            seen.add(strValue);
          }
        }
      });

      result[column.key] = Array.from(seen).slice(0, 50);
    });

    return result;
  }, [columns, sortedData]);

  // Filtered data: OR within same column, AND across different columns
  const filteredData = useMemo(() => {
    if (!filters.length) return sortedData;

    const filtersByColumn = new Map<string, TableFilter[]>();

    for (const filter of filters) {
      const group = filtersByColumn.get(filter.columnKey) || [];

      group.push(filter);
      filtersByColumn.set(filter.columnKey, group);
    }

    return sortedData.filter((row) =>
      [...filtersByColumn.entries()].every(([columnKey, columnFilters]) => {
        const cell = row[columnKey as keyof EmployeeData];

        if (cell === undefined || cell === null) return false;

        const cellStr = String(cell).toLowerCase();

        return columnFilters.some((filter) =>
          cellStr.includes(filter.value.toLowerCase()),
        );
      }),
    );
  }, [filters, sortedData]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;


return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  // Auto-adjust page when data changes
  useEffect(() => {
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    if (page >= totalPages && totalPages > 0) {
      setPage(totalPages - 1);
    }
    if (totalPages === 0 && page !== 0) {
      setPage(0);
    }
  }, [filteredData, page, rowsPerPage]);

  // Sync selection with available data
  useEffect(() => {
    setSelectedKeys((prev) => {
      const availableKeys = new Set(
        localPersonnelData.map((employee) => String(employee.key)),
      );
      const next = new Set<string>();

      prev.forEach((key) => {
        if (availableKeys.has(key)) {
          next.add(key);
        }
      });

return next;
    });
  }, [localPersonnelData]);

  // Visible keys for selection
  const visibleKeys = useMemo(
    () => paginatedData.map((row) => String(row.key)),
    [paginatedData],
  );

  const allVisibleSelected =
    visibleKeys.length > 0 && visibleKeys.every((key) => selectedKeys.has(key));
  const someVisibleSelected =
    visibleKeys.some((key) => selectedKeys.has(key)) && !allVisibleSelected;

  // Pagination info
  const totalRecords = filteredData.length;
  const pageStart = totalRecords === 0 ? 0 : page * rowsPerPage + 1;
  const pageEnd =
    totalRecords === 0 ? 0 : Math.min((page + 1) * rowsPerPage, totalRecords);

  // Filter actions
  const addFilter = useCallback((columnKey: string, value: string): void => {
    const trimmedValue = value.trim();

    if (!trimmedValue) return;

    setFilters((prev) => {
      const exists = prev.some(
        (filter) =>
          filter.columnKey === columnKey &&
          filter.value.toLowerCase() === trimmedValue.toLowerCase(),
      );

      if (exists) return prev;

      return [
        ...prev,
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          columnKey,
          value: trimmedValue,
        },
      ];
    });
    setPage(0);
  }, []);

  const removeFilter = useCallback((id: string): void => {
    setFilters((prev) => prev.filter((filter) => filter.id !== id));
    setPage(0);
  }, []);

  const removeFilterValue = useCallback((columnKey: string, value: string): void => {
    setFilters((prev) =>
      prev.filter(
        (filter) =>
          !(
            filter.columnKey === columnKey &&
            filter.value.toLowerCase() === value.toLowerCase()
          ),
      ),
    );
    setPage(0);
  }, []);

  const toggleFilterValue = useCallback(
    (columnKey: string, value: string): void => {
      const exists = filters.some(
        (filter) =>
          filter.columnKey === columnKey &&
          filter.value.toLowerCase() === value.toLowerCase(),
      );

      if (exists) {
        removeFilterValue(columnKey, value);
      } else {
        addFilter(columnKey, value);
      }
    },
    [filters, addFilter, removeFilterValue],
  );

  const clearFilters = useCallback((): void => {
    setFilters([]);
    setPage(0);
  }, []);

  const clearColumnFilters = useCallback((columnKey: string): void => {
    setFilters((prev) => prev.filter((filter) => filter.columnKey !== columnKey));
    setPage(0);
  }, []);

  // Column filter menu actions
  const openColumnFilterMenu = useCallback(
    (columnKey: string, anchorEl: HTMLElement): void => {
      setFilterMenuColumnKey(columnKey);
      setFilterMenuAnchorEl(anchorEl);
      setFilterMenuValue("");
    },
    [],
  );

  const closeColumnFilterMenu = useCallback((): void => {
    setFilterMenuAnchorEl(null);
    setFilterMenuColumnKey(null);
    setFilterMenuValue("");
  }, []);

  const handleApplyColumnFilter = useCallback((): void => {
    if (!filterMenuColumnKey) return;
    addFilter(filterMenuColumnKey, filterMenuValue);
    setFilterMenuValue("");
  }, [filterMenuColumnKey, filterMenuValue, addFilter]);

  const handleSelectColumnValue = useCallback(
    (value: string): void => {
      if (!filterMenuColumnKey) return;
      toggleFilterValue(filterMenuColumnKey, value);
    },
    [filterMenuColumnKey, toggleFilterValue],
  );

  const handleClearColumnFilters = useCallback((): void => {
    if (!filterMenuColumnKey) return;
    clearColumnFilters(filterMenuColumnKey);
  }, [filterMenuColumnKey, clearColumnFilters]);

  // Selection actions
  const toggleRowSelection = useCallback(
    (rowKey: string, isSelected: boolean): void => {
      setSelectedKeys((prev) => {
        const next = new Set(prev);

        if (isSelected) {
          next.add(rowKey);
        } else {
          next.delete(rowKey);
        }

return next;
      });
    },
    [],
  );

  const toggleSelectAllVisible = useCallback(
    (checked: boolean): void => {
      const keysOnPage = paginatedData.map((row) => String(row.key));

      setSelectedKeys((prev) => {
        const next = new Set(prev);

        if (checked) {
          keysOnPage.forEach((key) => next.add(key));
        } else {
          keysOnPage.forEach((key) => next.delete(key));
        }

return next;
      });
    },
    [paginatedData],
  );

  const resetSelection = useCallback((): void => {
    setSelectedKeys(new Set<string>());
  }, []);

  return {
    // Pagination
    page,
    setPage,
    rowsPerPage,

    // Sorting
    sortedData,

    // Filtering
    filters,
    filteredData,
    paginatedData,
    uniqueValuesByColumn,
    filterMenuAnchorEl,
    filterMenuColumnKey,
    filterMenuValue,
    setFilterMenuValue,

    // Selection
    selectedKeys,
    visibleKeys,
    allVisibleSelected,
    someVisibleSelected,

    // Pagination info
    totalRecords,
    pageStart,
    pageEnd,

    // Actions
    addFilter,
    removeFilter,
    toggleFilterValue,
    clearFilters,
    clearColumnFilters,
    openColumnFilterMenu,
    closeColumnFilterMenu,
    handleApplyColumnFilter,
    handleSelectColumnValue,
    handleClearColumnFilters,
    toggleRowSelection,
    toggleSelectAllVisible,
    resetSelection,
  };
};
